import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineSearch } from "react-icons/ai";
import { MdComment } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import CommentModal from "../../../components/comment-modal/comment-modal";
import Pagination from "../../../components/content/pagination/pagination";
import FilterForm from "../../../components/home-components/filter-form/filter-form";
import Header from "../../../components/home-components/header/header";
import Slider from "../../../components/home-components/slider";
import Table from "../../../components/home-components/table";
import TableCounter from "../../../components/home-components/table/components/counter";
import TableFilter, {
  FilterOption,
} from "../../../components/home-components/table/components/filter";
import { HeaderItem } from "../../../components/home-components/table/components/header";
import RecruiterSelect from "../../../components/home-components/table/components/recruiter-select";
import { SelectOption } from "../../../components/home-components/table/components/select";
import SelectedStatus from "../../../components/home-components/table/components/selected-status";
import { selectedSchema } from "../../../components/home-components/table/components/selected-status/selected-status.utils";
import Tags from "../../../components/home-components/table/components/tag";
import {
  DataCell,
  FilterContainer,
  InfoTableContainer,
  TableRow,
} from "../../../components/home-components/table/index.styled";
import { getCandidateSalary } from "../../../components/home-components/table/utils/table.utils";
import Modal from "../../../components/modal/modal";
import { BulletStatus } from "../../../components/talent/components/feedback-status/feedback-status.styled";
import Talent from "../../../components/talent/talent";
import TooltipWarning from "../../../components/tooltip-warning";
import { useModal, useQuery } from "../../../hooks";
import useCandidateStatus from "../../../hooks/use-candidate-status";
import useChat from "../../../hooks/use-chat";
import { Candidate, jobOfferStatusTypes } from "../../../models/candidate.model";
import HomeDashboardPageQuery from "../../../models/home-dashboard-page-query.model";
import { ProcessStatus } from "../../../models/last-action-status.enum";
import { OriginFilterType } from "../../../models/origin-filter.model";
import { OriginType } from "../../../models/track-type.enum";
import { AppState } from "../../../store/app-state";
import { clearCandidateDetail } from "../../../store/candidate/candidate-actions";
import {
  fetchCompanyPlan,
  fetchProcessSteps,
  fetchRecruiters,
  fetchSelectedCandidatesProcessSteps,
} from "../../../store/company/company-actions";
import {
  fetchEventData,
  fetchEventTracks,
  fetchOriginFilter,
  fetchSelectedCandidates,
  fetchTrackCandidates,
} from "../../../store/event/event-actions";
import {
  fetchSetInvisible,
  fetchSetVisible,
} from "../../../store/load/load-actions";
import { getCurrentPlan } from "../../../store/plan/plan-actions";
import { queryLoaded } from "../../../utils/query.utils";
import {
  CandidatesNotFound,
  CommentButton,
  Container,
  Content,
  SelectedCandidatesContainer,
  ViewDesktopFilters,
} from "./selected-candidates-page.styled";
import { Events } from "../../../utils/constants";
import useUmamiTracking from "../../../hooks/use-umami-tracking";
import { useFilterContext } from "../../../contexts/filter";

const eventSlug: string = "talent-match";
const modalTalent = document.getElementById("modal-talent");

const SelectedCandidatesPage: React.FC = () => {
  useChat();
  const theme = useTheme();
  const { t } = useTranslation("private-home-page");
  const dispatch = useDispatch();
  const [query, setQuery] = useQuery<HomeDashboardPageQuery>();
  const user = useSelector((state: AppState) => state.authState.user);
  const devTableRef = useRef<HTMLDivElement>(null);
  const { candidateDetailData } = useSelector(
    (state: AppState) => state.candidateState
  );
  const { selectedCandidatesData } = useSelector(
    (state: AppState) => state.eventState
  );
  const { isShown, toggle } = useModal();
  const { isShown: showComment, toggle: toggleComment } = useModal();
  const [index, setIndex] = useState<number>(-1);

  const candidate = useMemo((): Candidate | undefined => {
    return index >= 0 ? selectedCandidatesData.data?.results[index] : undefined;
  }, [selectedCandidatesData, index]);

  const candidateStatus = useCandidateStatus({
    candidateId: candidate?.id || "",
  });
  const { currentEventTrackData, originFilterData } = useSelector(
    (state: AppState) => state.eventState
  );
  const companyId = useSelector(
    (state: AppState) => state.authState?.user?.corporate?.id
  );
  const recruitersFromCompany = useSelector(
    (state: AppState) => state.companyState.recruiters
  );
  const recruiterOptions: SelectOption[] = useMemo(() => {
    return (
      recruitersFromCompany?.map((recruiter: any) => ({
        value: recruiter.uuid,
        label: recruiter.name,
      })) || []
    );
  }, [recruitersFromCompany]);
  const pushUmami = useUmamiTracking();
  const { filters: localFilter } = useFilterContext();

  useEffect(() => {
    if (!selectedCandidatesData.loading) {
      const uuids = selectedCandidatesData.data?.results?.map(
        (candidate) => candidate.user_id
      );
      uuids && dispatch(fetchSelectedCandidatesProcessSteps(uuids));
      dispatch(fetchSetInvisible());
    }
  }, [
    selectedCandidatesData.loading,
    selectedCandidatesData.data?.results,
    dispatch,
  ]);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchProcessSteps(companyId));
      dispatch(fetchRecruiters(companyId));
    }
  }, [dispatch, companyId]);

  const handleSearch = useCallback(
    (query: HomeDashboardPageQuery) => {
      dispatch(fetchSelectedCandidates(eventSlug, query));
      dispatch(fetchSetVisible());
      dispatch(fetchEventData(eventSlug));
      dispatch(fetchEventTracks(eventSlug));
      dispatch(fetchOriginFilter(eventSlug));
      dispatch(fetchCompanyPlan());
      dispatch(getCurrentPlan());
      pushUmami(Events.SEARCH, {
        filters: localFilter,
        locale: 'selecteds'
      }, user);
    },
    [dispatch, pushUmami, localFilter, user]
  );

  const handlePartialSearch = useCallback(
    (query: HomeDashboardPageQuery) => {
      dispatch(fetchSelectedCandidates(eventSlug, query));
      dispatch(fetchSetVisible());
    },
    [dispatch]
  );

  const reloadRanking = useCallback(() => {
    const q = { ...query };
    handleSearch(q);
  }, [query, handleSearch]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const keys = [...q.keys()];
    if (keys.length === 0 && user) {
      const currency = user?.corporate?.currency || "BRL";
      handleSearch({ currency });
      setQuery({ currency });
    }
  }, [dispatch, user, setQuery, handleSearch]);

  useEffect(() => {
    const loaded = queryLoaded(query);
    if (
      !selectedCandidatesData.data &&
      !selectedCandidatesData.loading &&
      !selectedCandidatesData.error &&
      user &&
      loaded
    ) {
      let q = { ...query };
      if (!q.currency) {
        q.currency = user?.corporate?.currency || "BRL";
      }
      handleSearch(q);
      setQuery(q);
    }
  }, [query, handleSearch, selectedCandidatesData, user, setQuery]);

  const handlePageChange = useCallback(
    (page: number) => {
      query.selectedCandidatesPage = page.toString();
      setQuery(query);
      handleSearch(query);

      if (devTableRef.current) {
        window.scrollTo({
          behavior: "smooth",
          top: devTableRef.current.offsetTop,
        });
      }
    },
    [query, setQuery, devTableRef, handleSearch]
  );

  const handleOpen = useCallback(() => {
    toggle();
  }, [toggle]);

  const handleClose = useCallback(() => {
    if (currentEventTrackData.data?.register) {
      // TODO: Se possível toda atualização vier pelo realtime também
      dispatch(fetchTrackCandidates(currentEventTrackData.data.uuid, query));
    }
    dispatch(clearCandidateDetail());
    toggle();
  }, [dispatch, toggle, currentEventTrackData, query]);

  const handleModal = useCallback(
    (candidate: Candidate, candidateStatus, dataCellBlocked?: boolean) => {
      (!candidateStatus?.hired ||
        candidateStatus?.hired === user?.corporate.id ||
        candidate.company_hired === user?.corporate.id) &&
        !dataCellBlocked &&
        handleOpen();
    },
    [handleOpen, user]
  );

  const handleCellClick = useCallback((key: number, candidate: Candidate, block?: boolean) => {
    setIndex(key);
    handleModal(candidate, candidateStatus, block);
  }, [candidateStatus, handleModal]);

  const filterOrigins: FilterOption[] = useMemo(() => {
    return [
      {
        value: t("ranking-table.filters.all"),
        action: () => {
          const q = { ...query, origin: undefined, origin_type: undefined };
          setQuery(q);
          handleSearch(q);
        },
      },
      {
        value: t("ranking-table.filters.ranking"),
        action: () => {
          const q = {
            ...query,
            origin: "ranking",
            origin_type: OriginFilterType.RANKING,
          };
          setQuery(q);
          handleSearch(q);
        },
      },
      ...originFilterData.map(
        (origin): FilterOption => ({
          value: origin.name,
          action: () => {
            const q = {
              ...query,
              origin: origin.id,
              origin_type: origin.origin_type,
            };
            setQuery(q);
            handleSearch(q);
          },
        })
      ),
    ];
  }, [originFilterData, query, setQuery, t, handleSearch]);

  const originFilterInitial: FilterOption = useMemo(() => {
    if (!query.origin || query.origin === undefined) {
      return filterOrigins[0];
    }
    if (query.origin === "ranking") {
      return filterOrigins[1];
    }
    const origin = originFilterData.find(
      (origin) => origin.id === query.origin
    );
    if (!origin) {
      return filterOrigins[0];
    }
    return {
      value: origin.name,
      action: () =>
        setQuery({
          ...query,
          origin: origin.id,
          origin_type: origin.origin_type,
        }),
    };
  }, [query, originFilterData, filterOrigins, setQuery]);

  const filterOptions: FilterOption[] = [
    {
      value: "show-all",
      action: () => {
        const q = { ...query, status: undefined };
        setQuery(q);
        handleSearch(q);
      },
    },
    {
      value: "my-candidates",
      action: () => {
        const q = { ...query, status: "my_candidates" };
        setQuery(q);
        handleSearch(q);
      },
    },
  ];

  const buildStatusFilterOptions = useCallback((en_um, offer=false) => {
    const options: {
      value: string;
      label: JSX.Element;
    }[] = [];
    
    for(let s in en_um) {
      const status = s.toLowerCase(); 
      options.push({
        value: status,
        label: (
          <BulletStatus
            color={selectedSchema[status].color}
            offer={offer}
          >
            <p>{t(selectedSchema[status].title)}</p>
          </BulletStatus>
        )
      })
    }

    return options;
  }, [t]);

  const tableHeaderItems: HeaderItem[] = useMemo(() => ([
    {
      value: "last-action",
      width: "10%",
      query_param: "process_status",
      filter: buildStatusFilterOptions(ProcessStatus, false),
    },
    {
      value: "status",
      width: "15%",
      query_param: "offer_status",
      filter: buildStatusFilterOptions(jobOfferStatusTypes, true),
    },
    {
      value: "candidate",
      width: "20%",
    },
    {
      value: "main-skills",
      width: "15%",
    },
    {
      value: "experience",
      width: "10%",
    },
    {
      value: "wage-claim",
      width: "15%",
    },
    {
      value: "responsable",
      width: "10%",
    },
    {
      value: "comment",
      width: "5%",
    },
    {
      value: "tags",
      width: "10%",
    }
  ]), [buildStatusFilterOptions]);

  const getOriginalWageClaim = useCallback(
    (candidate: Candidate) => {
      return candidate?.wage_claims?.[0]?.brl
        ? t("wage-claim-converted-original", {
            originalValue: parseFloat(
              candidate.wage_claims[0].brl
            ).toLocaleString(),
          })
        : t("wage-claim-converted");
    },
    [t]
  );

  const orderOptions: FilterOption[] = [
    {
      value: "createdByDesc",
      action: () => {
        setQuery({ ...query, orderBy: "createdByDesc" });
        handleSearch({ ...query, orderBy: "createdByDesc" });
      },
    },
    {
      value: "createdByAsc",
      action: () => {
        setQuery({ ...query, orderBy: "createdByAsc" });
        handleSearch({ ...query, orderBy: "createdByAsc" });
      },
    },
  ];

  return (
    <SelectedCandidatesContainer>
      <Modal
        width={960}
        isShown={isShown}
        toggle={handleOpen}
        target={modalTalent}
        handleClose={handleClose}
        closeOnEsc={true}
        background={"#F3F3F3"}
      >
        <Slider
          onNext={() => setIndex(index + 1)}
          onPrev={() => setIndex(index - 1)}
          showNext={
            index < (selectedCandidatesData.data?.count || 0) - 1 &&
            !candidateDetailData.loading
          }
          showPrev={index !== 0 && !candidateDetailData.loading}
        >
          <Talent
            talentId={
              candidate
                ? candidate?.is_pool && candidate?.user_id
                  ? candidate?.user_id
                  : candidate?.id
                : ""
            }
            userId={candidate?.user_id || ""}
            trackId={candidate?.selection?.source_id}
            ecosystem={query.ecosystem || ""}
            isNotifyOpenCandidateDetail={!candidate?.selection}
            showJobOrigin={candidate?.selection?.origin_type === OriginType.JOB}
            jobName={candidate?.selection?.origin_name}
            currencyIso={query.currency}
          />
        </Slider>
      </Modal>
      <Modal
        width={850}
        isShown={showComment}
        toggle={toggleComment}
        target={modalTalent}
        background={"#fff"}
      >
        <CommentModal candidateId={candidate?.user_id} />
      </Modal>
      <Container>
        <Header
          title={t("tabs.selecteds.title")}
          subtitle={user?.corporate?.name}
          isSelecteds={true}
        />
        <ViewDesktopFilters id="filters">
          <FilterForm
            initialMaxSalary={50000}
            isSelectedsCandidate={true}
            searchCallback={(query) => {
              handleSearch(query);
            }}
            disableKeyboard={isShown}
          />
        </ViewDesktopFilters>
        <Content>
          <InfoTableContainer id="selected-candidates-info">
            <TableCounter
              count={selectedCandidatesData.data?.results.length || 0}
              total={selectedCandidatesData.data?.count || 0}
            />
            <FilterContainer>
              <TableFilter
                label={"origin"}
                options={filterOrigins}
                initial={originFilterInitial}
                i18n_item={false}
              />
              <TableFilter
                label={"show"}
                options={filterOptions}
                initial={
                  query?.status === "my_candidates"
                    ? filterOptions[1]
                    : filterOptions[0]
                }
              />
              <TableFilter
                label={"order-by"}
                options={orderOptions}
                initial={
                  query?.orderBy === "createdByAsc"
                    ? orderOptions[1]
                    : orderOptions[0]
                }
              />
            </FilterContainer>
          </InfoTableContainer>
          <Table
            headerItems={tableHeaderItems}
            id="selected-candidates-table"
            handleSearch={handlePartialSearch}
          >
            {selectedCandidatesData.data?.results.map((candidate, key) => (
              <TableRow key={key}>
                <DataCell 
                  onClick={() => handleCellClick(key, candidate)}
                >
                  <SelectedStatus
                    talentStatus={candidate.last_action?.status}
                  />
                </DataCell>
                <DataCell
                  onClick={() => handleCellClick(key, candidate)}
                >
                  <SelectedStatus
                    talentStatus={candidate?.job_offer?.status}
                    offer={true}
                  />
                </DataCell>
                <DataCell
                  onClick={() => handleCellClick(key, candidate)}
                  color={theme.colors.primaryBlue.main}
                  bold={600}
                >
                  {candidate?.full_name}
                </DataCell>
                <DataCell
                  onClick={() => handleCellClick(key, candidate)}
                >
                  {candidate.main_skills
                    ?.map((skill) => skill?.name)
                    .join("/")}
                </DataCell>
                <DataCell
                  onClick={() => handleCellClick(key, candidate)}
                >
                  {candidate?.experience_time_tech}
                </DataCell>
                <DataCell
                  onClick={() => handleCellClick(key, candidate)}
                >
                  {getCandidateSalary(candidate, t)}{" "}
                  {candidate.wage_claim_was_converted && (
                    <TooltipWarning
                      message={getOriginalWageClaim(candidate)}
                      marginLeft=".5rem"
                    />
                  )}
                </DataCell>
                <DataCell
                  onClick={() => handleCellClick(key, candidate, true)}
                >
                  <RecruiterSelect
                    options={recruiterOptions}
                    candidate={candidate}
                    actionId={candidate.selection?.action_id?.toString()}
                  />
                </DataCell>
                <DataCell>
                  <CommentButton
                    onClick={() => {
                      setIndex(key);
                      toggleComment();
                    }}
                  >
                    <MdComment />
                  </CommentButton>
                </DataCell>
                <DataCell>
                  {candidate.tags && (
                    <Tags
                      tags={candidate.tags}
                      candidateId={
                        candidate?.user_id
                      }
                      tagsChanged={() => {
                        reloadRanking();
                      }}
                      layout="table"
                    />
                  )}
                </DataCell>
              </TableRow>
            ))}
          </Table>
          {selectedCandidatesData.data &&
            selectedCandidatesData.data.results?.length > 0 && (
              <Pagination
                currentPage={selectedCandidatesData.data?.current_page}
                pageNumbers={selectedCandidatesData.data?.total_pages}
                onPageChange={handlePageChange}
              />
            )}
            {(!selectedCandidatesData.data?.results || 
              selectedCandidatesData.data?.results.length === 0) && (
              <CandidatesNotFound>
                <AiOutlineSearch className="icon-search" />
                {t("tabs.selecteds.noTalentSelected")}
              </CandidatesNotFound>
            )}
        </Content>
      </Container>
    </SelectedCandidatesContainer>
  );
};

export default SelectedCandidatesPage;
